'use client';

import { useCallback, useEffect, useRef } from 'react';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import customFetch from '@/lib/utils/fetch';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import { WithdrawStatus, type WithdrawRecord, type WithdrawRecordsResponse } from './use-withdraw';

// 交易记录查询（pUSD 转入/转出）。GET /api/live-trading/transaction?address=0x...（需登录态）
// address 必填；可选 maxCount（1-100，默认 10）。游标分页：把上一页
// pagination.next_page_params 平铺为 query 参数请求下一页（Alchemy 源为
// { inPageKey, outPageKey }，Blockscout 兜底源为其游标对象），null 表示无更多数据。
const TRANSACTION_PATH = '/api/live-trading/transaction';

/** 交易记录（字段与路由 parseTransactions / parseBlockscoutTransactions 对应） */
export interface TransactionRecord {
  /** 相对查询地址的方向：in=转入，out=转出 */
  direction: 'in' | 'out';
  transaction_hash: string | null;
  block_number: number | null;
  timestamp: string | null;
  from_address: string | null;
  to_address: string | null;
  /** 最小单位金额字符串 */
  amount_raw: string | null;
  /** 已按精度换算的金额 */
  amount: number | null;
  /** Alchemy 源为 number，Blockscout 源为 string */
  decimals: number | string | null;
  token_address: string | null;
  token_symbol: string | null;
  type: string | null;
}

/** 下一页游标：Alchemy 源 { inPageKey, outPageKey }；Blockscout 源为其游标对象 */
export type TransactionNextPageParams = Record<string, unknown>;

export interface TransactionRecordsResponse {
  success: boolean;
  /** 数据来源：Alchemy 主源 / Blockscout 兜底 */
  source: 'alchemy' | 'blockscout';
  data: TransactionRecord[];
  pagination: {
    /** null 表示无更多数据 */
    next_page_params: TransactionNextPageParams | null;
  };
}

async function fetchTransactionRecords(params: {
  address: string;
  maxCount: number;
  nextPageParams: TransactionNextPageParams | null;
}): Promise<TransactionRecordsResponse> {
  const search = new URLSearchParams({
    address: params.address,
    maxCount: String(params.maxCount),
  });
  for (const [key, value] of Object.entries(params.nextPageParams ?? {})) {
    if (value != null) search.set(key, String(value));
  }
  const res = await customFetch(`${TRANSACTION_PATH}?${search.toString()}`);
  if (!(res instanceof Response)) throw res instanceof Error ? res : new Error('fetch failed');
  if (!res.ok) throw new Error(`transaction records request failed: ${res.status}`);
  return res.json() as Promise<TransactionRecordsResponse>;
}

const WITHDRAW_APPLY_PATH = '/api/live-trading/withdraw/apply';

/** 默认合并的提现申请状态：3=提现失败,4=提现取消,5=审核拒绝 */
const DEFAULT_WITHDRAW_STATUSES = [
  WithdrawStatus.Failed,
  WithdrawStatus.Cancelled,
  WithdrawStatus.Rejected,
];

async function fetchWithdrawRecords(params: {
  limit: number;
  offset: number;
  statuses: WithdrawStatus[];
}): Promise<WithdrawRecordsResponse> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.statuses.length > 0) search.set('status', params.statuses.join(','));
  const res = await customFetch(`${WITHDRAW_APPLY_PATH}?${search.toString()}`);
  if (!(res instanceof Response)) throw res instanceof Error ? res : new Error('fetch failed');
  if (!res.ok) throw new Error(`withdraw records request failed: ${res.status}`);
  return res.json() as Promise<WithdrawRecordsResponse>;
}

/** 单页数据 = Promise.all 并发拉取两个接口；null 表示该数据源已取完（本页跳过请求） */
interface HistoryPage {
  chain: TransactionRecordsResponse | null;
  withdraw: WithdrawRecordsResponse | null;
}

/** 合并记录：source 区分数据来源，sortTime（ms）为混合降序排序键 */
export type HistoryRecord =
  | { source: 'transaction'; sortTime: number; record: TransactionRecord }
  | { source: 'withdraw'; sortTime: number; record: WithdrawRecord };

export interface UseTransactionRecordsOptions {
  /** 查询地址；缺省回落到系统钱包，两者都没有则不发起请求 */
  address?: string;
  /** 每页条数（1-100，两个接口各取此数量），默认 10 */
  maxCount?: number;
  /** 合并的提现申请状态，默认 [3,4,5]（失败/取消/拒绝） */
  withdrawStatuses?: WithdrawStatus[];
  enabled?: boolean;
  swr?: SWRInfiniteConfiguration<HistoryPage, Error>;
}

/**
 * 查询交易历史（useSWRInfinite）：每页 Promise.all 并发拉取链上转账记录（游标分页）
 * 与提现申请记录（offset 分页，默认过滤失败/取消/拒绝），两源按时间降序混合返回。
 * 任一数据源取完后后续页自动跳过该源；`loadMore` / `setSentinelRef` 触发加载下一页。
 */
export function useTransactionRecords(options: UseTransactionRecordsOptions = {}) {
  const { wallets } = useTrackingWallets();
  const {
    maxCount = 10,
    withdrawStatuses = DEFAULT_WITHDRAW_STATUSES,
    enabled = true,
    swr,
  } = options;
  const address = options.address?.trim() || wallets.systemWallet;
  const statusKey = withdrawStatuses.join(',');

  const getKey = useCallback(
    (pageIndex: number, previousPageData: HistoryPage | null) => {
      if (!enabled || !address) return null;
      // 各数据源独立判断是否还有下一页；上一页该源为 null（已跳过）视为取完
      const chainDone =
        previousPageData != null &&
        (previousPageData.chain == null ||
          previousPageData.chain.pagination?.next_page_params == null);
      const withdrawDone =
        previousPageData != null &&
        (previousPageData.withdraw == null || !previousPageData.withdraw.pagination?.has_more);
      if (chainDone && withdrawDone) return null;
      return [
        'live-history',
        address,
        maxCount,
        statusKey,
        chainDone ? null : (previousPageData?.chain?.pagination?.next_page_params ?? null),
        chainDone,
        // 提现侧每页都取满 maxCount，offset 随页号线性递增
        withdrawDone ? 0 : pageIndex * maxCount,
        withdrawDone,
      ] as const;
    },
    [enabled, address, maxCount, statusKey],
  );

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    HistoryPage,
    Error
  >(
    getKey,
    async ([
      ,
      addr,
      pageSize,
      statuses,
      chainCursor,
      chainDone,
      withdrawOffset,
      withdrawDone,
    ]: readonly [
      string,
      string,
      number,
      string,
      TransactionNextPageParams | null,
      boolean,
      number,
      boolean,
    ]) => {
      const [chain, withdraw] = await Promise.all([
        chainDone
          ? null
          : fetchTransactionRecords({
              address: addr,
              maxCount: pageSize,
              nextPageParams: chainCursor,
            }),
        withdrawDone
          ? null
          : fetchWithdrawRecords({
              limit: pageSize,
              offset: withdrawOffset,
              statuses: statuses ? (statuses.split(',').map(Number) as WithdrawStatus[]) : [],
            }),
      ]);
      return { chain, withdraw };
    },
    { ...swr },
  );

  // 两个来源打上 source/sortTime 标记后按时间降序混合
  const records: HistoryRecord[] = (data ?? [])
    .flatMap((page) => [
      ...(page?.chain?.data ?? []).map((record) => ({
        source: 'transaction' as const,
        sortTime: record.timestamp ? new Date(record.timestamp).getTime() : 0,
        record,
      })),
      ...(page?.withdraw?.data ?? []).map((record) => ({
        source: 'withdraw' as const,
        sortTime: new Date(record.createdAt).getTime(),
        record,
      })),
    ])
    .sort((a, b) => b.sortTime - a.sortTime);

  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage
    ? lastPage.chain?.pagination?.next_page_params != null ||
      (lastPage.withdraw?.pagination?.has_more ?? false)
    : false;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) setSize((s) => s + 1);
  }, [isValidating, hasMore, setSize]);

  // IntersectionObserver sentinel：绑定到列表底部元素，进入视口自动加载下一页
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setSentinelRef = useCallback(
    (el: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!el) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(el);
    },
    [loadMore],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    records,
    hasMore,
    error,
    isLoading,
    isValidating,
    page: size,
    loadMore,
    setSentinelRef,
    refresh: mutate,
  };
}

export default useTransactionRecords;
